/**
 * MonthYearPicker -- Month and year popup selector.
 *
 * Stores value as YYYY-MM string (or empty string for "not set").
 * Supports an optional "Present" selection and a minValue constraint
 * to enforce that a "from" date is always earlier than "to".
 *
 * ADR-022: UI Primitives reside in components/ui/.
 */
import { useEffect, useRef, useState, useId } from 'react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export interface MonthYearPickerProps {
  /** Current value in YYYY-MM format, or empty string for none */
  value: string;
  /** Called with YYYY-MM string, or empty string when cleared */
  onChange: (value: string) => void;
  /** Visible label rendered above the trigger */
  label?: string;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Show a "Present" option (for end dates on current roles) */
  allowPresent?: boolean;
  /** Whether "Present" is the current selection */
  isPresent?: boolean;
  /** Called when the Present toggle changes */
  onPresentChange?: (isPresent: boolean) => void;
  /** Minimum selectable value in YYYY-MM format */
  minValue?: string;
  /** Disable the entire picker */
  disabled?: boolean;
  /** Error message */
  error?: string;
}

function parseYearMonth(val: string): { year: number; month: number } | null {
  if (!val || val.length < 7) return null;
  const year = parseInt(val.slice(0, 4), 10);
  const month = parseInt(val.slice(5, 7), 10);
  if (isNaN(year) || isNaN(month)) return null;
  return { year, month };
}

function formatDisplay(val: string): string {
  const parsed = parseYearMonth(val);
  if (!parsed) return '';
  const monthName = MONTHS[parsed.month - 1] ?? '';
  return `${monthName} ${parsed.year}`;
}

export function MonthYearPicker({
  value,
  onChange,
  label,
  placeholder = 'Select month',
  allowPresent = false,
  isPresent = false,
  onPresentChange,
  minValue,
  disabled = false,
  error,
}: MonthYearPickerProps) {
  const id = useId();
  const errorId = error ? `${id}-error` : undefined;

  const now = new Date();
  const currentYear = now.getFullYear();

  const parsedValue = parseYearMonth(value);
  const parsedMin = parseYearMonth(minValue ?? '');

  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(parsedValue?.year ?? currentYear);

  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Sync view year when value changes externally
  useEffect(() => {
    if (parsedValue) setViewYear(parsedValue.year);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleTriggerClick() {
    if (disabled) return;
    if (isPresent) return; // "Present" is selected, can't open picker
    setViewYear(parsedValue?.year ?? currentYear);
    setOpen((o) => !o);
  }

  function handleSelectMonth(month: number) {
    onChange(`${viewYear}-${String(month).padStart(2, '0')}`);
    setOpen(false);
  }

  function isMonthDisabled(month: number): boolean {
    if (!parsedMin) return false;
    if (viewYear < parsedMin.year) return true;
    if (viewYear === parsedMin.year && month < parsedMin.month) return true;
    return false;
  }

  function isMonthSelected(month: number): boolean {
    return parsedValue?.year === viewYear && parsedValue?.month === month;
  }

  const displayValue = isPresent ? 'Present' : (formatDisplay(value) || '');

  return (
    <div className="input-wrapper month-year-picker" ref={containerRef}>
      {label && (
        <label className="input-label" htmlFor={id}>
          {label}
        </label>
      )}

      <button
        id={id}
        type="button"
        className={`month-year-picker__trigger input ${error ? 'input--error' : ''} ${disabled ? 'month-year-picker__trigger--disabled' : ''}`}
        onClick={handleTriggerClick}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
      >
        <span className={displayValue ? '' : 'month-year-picker__placeholder'}>
          {displayValue || placeholder}
        </span>
        {!disabled && !isPresent && (
          <span className="month-year-picker__caret" aria-hidden="true">▾</span>
        )}
      </button>

      {open && (
        <div className="month-year-picker__popup" role="listbox" aria-label="Select month and year">
          <div className="month-year-picker__year-nav">
            <button
              type="button"
              className="month-year-picker__nav-btn"
              onClick={() => setViewYear((y) => y - 1)}
              aria-label="Previous year"
            >
              ‹
            </button>
            <span className="month-year-picker__year">{viewYear}</span>
            <button
              type="button"
              className="month-year-picker__nav-btn"
              onClick={() => setViewYear((y) => y + 1)}
              disabled={viewYear >= currentYear}
              aria-label="Next year"
            >
              ›
            </button>
          </div>

          <div className="month-year-picker__months">
            {MONTHS.map((name, idx) => {
              const month = idx + 1;
              const disabled = isMonthDisabled(month);
              const selected = isMonthSelected(month);
              return (
                <button
                  key={name}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={[
                    'month-year-picker__month',
                    selected ? 'month-year-picker__month--selected' : '',
                    disabled ? 'month-year-picker__month--disabled' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => !disabled && handleSelectMonth(month)}
                  disabled={disabled}
                >
                  {name}
                </button>
              );
            })}
          </div>

          {allowPresent && (
            <div className="month-year-picker__footer">
              <button
                type="button"
                className="month-year-picker__present-btn"
                onClick={() => {
                  onPresentChange?.(true);
                  onChange('');
                  setOpen(false);
                }}
              >
                Set as Present
              </button>
            </div>
          )}
        </div>
      )}

      {allowPresent && isPresent && onPresentChange && (
        <button
          type="button"
          className="month-year-picker__clear-present"
          onClick={() => onPresentChange(false)}
          disabled={disabled}
        >
          Pick a date instead
        </button>
      )}

      {error && (
        <span id={errorId} className="input-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
